import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CreditCard, Plus, Edit3, Trash2, Save, X, ToggleLeft, ToggleRight } from "lucide-react";

const CURRENCIES = ["jpy", "usd", "eur", "gbp", "krw", "twd", "hkd", "sgd", "thb", "vnd", "idr", "inr"];

const emptyForm = {
  id: "",
  label: "",
  description: "",
  amount: 0,
  currency: "jpy",
  active: true,
};

const fmtMoney = (amount, currency) => {
  const cur = (currency || "JPY").toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${amount} ${cur}`;
  }
};

export default function AdminPricing() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // package id being edited, or 'NEW'
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pricing");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setEditing("NEW");
    setForm(emptyForm);
  };

  const startEdit = (pkg) => {
    setEditing(pkg.id);
    setForm({
      id: pkg.id,
      label: pkg.label || "",
      description: pkg.description || "",
      amount: pkg.amount || 0,
      currency: pkg.currency || "jpy",
      active: pkg.active !== false,
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "NEW") {
        if (!/^[a-z0-9_-]{2,40}$/.test(form.id)) {
          toast.error("ID must be 2-40 lowercase letters / digits / _ / -");
          setSaving(false);
          return;
        }
        await api.post("/admin/pricing", form);
        toast.success("Pricing package created");
      } else {
        const { id, ...rest } = form;  // id is immutable
        await api.put(`/admin/pricing/${editing}`, rest);
        toast.success("Pricing package updated");
      }
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete pricing package "${id}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/pricing/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleActive = async (pkg) => {
    try {
      await api.put(`/admin/pricing/${pkg.id}`, { active: !(pkg.active !== false) });
      toast.success(pkg.active !== false ? "Disabled" : "Activated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8 lg:p-10">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">// PRICING</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight" style={{ fontWeight: 800 }}>
            支払い金額管理
          </h1>
          <p className="text-sm text-[#94A3B8] mt-2">
            プレミアム解放等の料金パッケージを管理します。Stripe Checkout は常にここから金額を取得します。
          </p>
        </div>
        <button
          type="button"
          onClick={startCreate}
          disabled={editing === "NEW"}
          data-testid="pricing-create-button"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#3B82F6] text-black font-mono text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#60A5FA] disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
          新規パッケージ
        </button>
      </div>

      {editing && (
        <div className="mb-6 p-6 bg-[#0F1525] border border-[#3B82F6]/40 rounded-md">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] mb-4">
            // {editing === "NEW" ? "新規パッケージ作成" : `編集: ${editing}`}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block label-mono mb-1.5">ID (slug) *</label>
              <input
                type="text"
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value })}
                disabled={editing !== "NEW"}
                placeholder="e.g. premium_unlock"
                data-testid="pricing-form-id"
                className="w-full bg-[#0A0E1A] border border-[#1E2A3F] p-2.5 text-sm text-white font-mono disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block label-mono mb-1.5">表示名 *</label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="IQ Log Premium Unlock"
                data-testid="pricing-form-label"
                className="w-full bg-[#0A0E1A] border border-[#1E2A3F] p-2.5 text-sm text-white font-mono"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block label-mono mb-1.5">説明</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                data-testid="pricing-form-description"
                className="w-full bg-[#0A0E1A] border border-[#1E2A3F] p-2.5 text-sm text-white font-mono"
              />
            </div>
            <div>
              <label className="block label-mono mb-1.5">金額 *</label>
              <input
                type="number"
                value={form.amount}
                min={0}
                onChange={(e) => setForm({ ...form, amount: parseInt(e.target.value || "0", 10) })}
                data-testid="pricing-form-amount"
                className="w-full bg-[#0A0E1A] border border-[#1E2A3F] p-2.5 text-sm text-white font-mono tabular-nums"
              />
              <p className="mt-1 text-[11px] text-[#64748B] font-mono">
                JPY/KRW/VND/IDR は整数(¥980 など)、USD/EUR は通貨単位($9.80 → 9.80)
              </p>
            </div>
            <div>
              <label className="block label-mono mb-1.5">通貨 *</label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                data-testid="pricing-form-currency"
                className="w-full bg-[#0A0E1A] border border-[#1E2A3F] p-2.5 text-sm text-white font-mono"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c.toUpperCase()}</option>
                ))}
              </select>
            </div>
            <label className="sm:col-span-2 flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                data-testid="pricing-form-active"
                className="accent-[#3B82F6]"
              />
              <span className="text-sm text-[#94A3B8]">有効(チェックを外すとフロントエンドから不可視・購入不能)</span>
            </label>
          </div>
          <div className="mt-5 flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="pricing-form-save"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B82F6] text-black font-mono text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#60A5FA] disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" strokeWidth={2.5} />
              {saving ? "保存中…" : "保存"}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              data-testid="pricing-form-cancel"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#1E2A3F] text-white font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#3B82F6] hover:text-[#3B82F6]"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2} />
              キャンセル
            </button>
          </div>
        </div>
      )}

      <div className="bg-[#0F1525] border border-[#1E2A3F] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="pricing-table">
          <thead className="border-b border-[#1E2A3F]">
            <tr className="text-[#64748B] font-mono text-[10px] uppercase tracking-[0.2em]">
              <th className="text-left p-3">ID</th>
              <th className="text-left p-3">表示名</th>
              <th className="text-right p-3">金額</th>
              <th className="text-left p-3">通貨</th>
              <th className="text-center p-3">状態</th>
              <th className="text-right p-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-[#64748B]">// loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-[#64748B]">No packages yet — create one above</td></tr>
            ) : (
              items.map((pkg) => {
                const isActive = pkg.active !== false;
                return (
                  <tr key={pkg.id} data-testid={`pricing-row-${pkg.id}`} className="border-b border-[#1E2A3F]/60 hover:bg-[#3B82F6]/5">
                    <td className="p-3 font-mono text-[11px] text-white">{pkg.id}</td>
                    <td className="p-3 text-white">{pkg.label}</td>
                    <td className="p-3 text-right font-mono text-[#F59E0B] tabular-nums font-bold">
                      {fmtMoney(pkg.amount, pkg.currency)}
                    </td>
                    <td className="p-3 font-mono text-[11px] text-[#94A3B8]">{(pkg.currency || "").toUpperCase()}</td>
                    <td className="p-3 text-center">
                      <button
                        type="button"
                        onClick={() => toggleActive(pkg)}
                        data-testid={`pricing-toggle-${pkg.id}`}
                        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em]"
                        style={{ color: isActive ? "#00FF94" : "#64748B" }}
                      >
                        {isActive ? <ToggleRight className="h-4 w-4" strokeWidth={2} /> : <ToggleLeft className="h-4 w-4" strokeWidth={2} />}
                        {isActive ? "active" : "disabled"}
                      </button>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => startEdit(pkg)}
                          data-testid={`pricing-edit-${pkg.id}`}
                          className="p-1.5 border border-[#1E2A3F] hover:border-[#3B82F6] hover:text-[#3B82F6] text-[#94A3B8]"
                          title="編集"
                        >
                          <Edit3 className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(pkg.id)}
                          data-testid={`pricing-delete-${pkg.id}`}
                          className="p-1.5 border border-[#1E2A3F] hover:border-[#EF4444] hover:text-[#EF4444] text-[#94A3B8]"
                          title="削除"
                        >
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex items-start gap-2 p-4 bg-[#0F1525] border border-[#1E2A3F] rounded-md">
        <CreditCard className="h-4 w-4 text-[#3B82F6] mt-0.5 shrink-0" strokeWidth={1.75} />
        <div className="text-xs text-[#94A3B8] leading-relaxed">
          <strong className="text-white">注意</strong>:
          現在 Stripe Checkout は <code className="text-[#3B82F6]">premium_unlock</code> パッケージのみ参照します。
          別IDで作成する場合は、フロントエンド側の対応が必要です。金額の変更は<strong className="text-[#F59E0B]">即時反映</strong>(キャッシュなし)されますので、テスト後に有効化してください。
        </div>
      </div>
    </div>
  );
}
